import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatPYG, formatDate } from "../../utils/format";
import { STATUS_LABELS, URGENCY_LABELS } from "./constants";

// Fix AQ (2026-09-05, URGENTE): "tampoco permite generar PDFs" (Sergio, desde el
// celular). `doc.save()` de jsPDF descarga con un <a download>, que Safari en
// iOS —y el navegador embebido de WhatsApp— ignora: al tocar el botón no pasa
// nada. Acá: en un navegador que soporta descargas se guarda igual que siempre;
// en el celular se abre el PDF en una pestaña nueva (se puede ver, compartir o
// guardar desde ahí). El `window.open` se hace dentro del mismo toque del
// usuario, si no iOS lo bloquea como popup.
export function guardarPDF(doc, nombre) {
  const archivo = String(nombre || "documento.pdf").replace(/[\\/:*?"<>|]/g, "-");
  const soportaDescarga = (() => {
    try {
      return "download" in document.createElement("a") && !/iPad|iPhone|iPod/.test(navigator.userAgent);
    } catch {
      return true;
    }
  })();
  if (soportaDescarga) {
    doc.save(archivo);
    return;
  }
  try {
    const url = URL.createObjectURL(doc.output("blob"));
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;   // popup bloqueado: se abre en la misma pestaña
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    doc.save(archivo);   // último recurso: el comportamiento de siempre
  }
}

// PDF REV COMPRAS MAT — Separar COD y FRENTE en visualización
// Acepta strings como "3. Alimentación de tableros (NYY)" y los separa.
export function splitFrenteCod(frenteStr) {
  if (!frenteStr) return { cod: "", label: "" };
  const m = String(frenteStr).match(/^\s*(\d+)\.\s*(.+)$/);
  if (m) return { cod: m[1], label: m[2].trim() };
  return { cod: "", label: String(frenteStr).trim() };
}

export function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("es-PY", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return dateStr; }
}

export function buildHistory(req) {
  if (req.history && req.history.length > 0) return req.history;
  const h = [];
  if (req.created_at) h.push({ action: "Pedido Creado", status: "pendiente", by: req.requested_by_name || "—", at: req.created_at, comment: "" });
  if (req.modification_requested_at) h.push({ action: "Devuelto para Modificación", status: "modificar", by: req.modification_requested_by || "—", at: req.modification_requested_at, comment: req.modification_comment || "" });
  if (req.approved_at) h.push({ action: "Aprobado", status: "aprobada", by: req.approved_by_name || "—", at: req.approved_at, comment: (req.approval_comment || "") + (req.apm_number ? ` [APM: ${req.apm_number}]` : "") });
  if (req.rejected_at) h.push({ action: "Rechazado", status: "rechazada", by: req.rejected_by_name || "—", at: req.rejected_at, comment: req.rejection_comment || "" });
  return h.sort((a, b) => new Date(a.at) - new Date(b.at));
}

// Fix ED (2026-09-24): `datos` (de datosPdfPedido) trae el presupuesto de referencia y,
// si el pedido tiene presupuesto, las filas con ítem, nivel y análisis: ahí la hoja va
// apaisada para que entren las columnas.
export function generatePedidoPDF(req, datos = null) {
  const conFilas = !!(datos && datos.filas);
  const doc = new jsPDF({ orientation: conFilas ? "l" : "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_BLUE = [46, 109, 164];

  doc.setFillColor(...CEN_BLUE);
  doc.rect(0, 0, pw, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 12);
  doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
  doc.text("Instalaciones Eléctricas  —  Asunción, Paraguay", 14, 19);
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text("PEDIDO DE MATERIALES", pw - 14, 13, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(req.number || "", pw - 14, 20, { align: "right" });

  doc.setTextColor(...CEN_BLUE);
  doc.setFontSize(8);
  const statusLabel = STATUS_LABELS[req.status] || req.status || "";
  doc.text(`Estado: ${statusLabel}`, 14, 30);
  doc.text(`Urgencia: ${URGENCY_LABELS[req.urgency] || req.urgency || "—"}`, pw - 14, 30, { align: "right" });

  let y = 42;
  doc.setFontSize(8.5);
  const col2 = pw / 2 + 4;
  const fieldRow = (x, fy, label, value) => {
    doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 80);
    doc.text(label + ":", x, fy);
    doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20);
    doc.text(String(value || "—"), x + 32, fy);
  };
  fieldRow(14, y, "Proyecto / Obra", req.project_name);
  fieldRow(col2, y, "Solicitado por", req.requested_by_name);
  fieldRow(14, y + 7, "Fecha Solicitud", formatDateTime(req.created_at));
  fieldRow(col2, y + 7, "Fecha Objetivo", req.target_date ? formatDate(req.target_date) : "—");
  // Fix ED (2026-09-24): presupuesto de referencia (código y nombre) y cliente.
  const ref = datos?.presupuesto || { codigo: req.presupuesto_codigo || req.pto_objetivo || "", nombre: "", cliente: req.lista_cliente_nombre || "" };
  fieldRow(14, y + 14, "Cliente", String(ref.cliente || "—").substring(0, conFilas ? 70 : 38));
  fieldRow(col2, y + 14, "Presupuesto", ref.codigo || "—");
  const extra = ref.nombre ? 7 : 0;
  if (ref.nombre) fieldRow(14, y + 21, "Nombre del Pto.", String(ref.nombre).substring(0, conFilas ? 120 : 80));
  if (req.notes) fieldRow(14, y + 21 + extra, "Observaciones", String(req.notes).substring(0, conFilas ? 120 : 50));

  doc.setDrawColor(...CEN_BLUE);
  doc.setLineWidth(0.3);
  doc.line(14, y + 27 + extra, pw - 14, y + 27 + extra);

  y += 31 + extra;
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_BLUE);
  doc.text("MATERIALES SOLICITADOS", 14, y);

  if (conFilas) {
    autoTable(doc, {
      startY: y + 3,
      head: [["#", "Categ.", "Descripción", "UM", "Cant.", "Ítem del Pto.", "Nivel", "Análisis", "Sector", "Frente", "Marca Rec."]],
      body: datos.filas.map((f, i) => [String(i + 1), f.categoria, f.descripcion, f.unidad, f.cantidad, f.item, f.nivel, f.analisis, f.sector, f.frente, f.marca]),
      headStyles: { fillColor: CEN_BLUE, textColor: [255, 255, 255], fontSize: 7.5, fontStyle: "bold" },
      bodyStyles: { fontSize: 7, textColor: [30, 30, 30], valign: "top" },
      alternateRowStyles: { fillColor: [237, 245, 255] },
      columnStyles: { 0: { cellWidth: 7 }, 1: { cellWidth: 17 }, 2: { cellWidth: 44 }, 3: { cellWidth: 10 }, 4: { cellWidth: 14 },
        5: { cellWidth: 44 }, 6: { cellWidth: 18 }, 7: { cellWidth: 60 }, 8: { cellWidth: 18 }, 9: { cellWidth: 20 }, 10: { cellWidth: 17 } },
      margin: { left: 14, right: 14 }
    });
  } else autoTable(doc, {
    startY: y + 3,
    head: [["#", "Descripción", "UM", "Cant.", "Sector", "Frente", "Marca Rec."]],
    // Fix DJ (2026-09-19): mismo criterio que la pantalla — un material imputado a
    // varios ítems del presupuesto sale en UNA fila con la cantidad total. El PDF
    // del pedido es lo que se manda a comprar: seis filas de la misma caja con
    // 66,6667 cada una no se puede cotizar.
    body: (() => {
      const grupos = [];
      const porClave = new Map();
      (req.items || []).forEach((item) => {
        const k = [item?.material_name, item?.unit, item?.sector, item?.front, item?.marca_recomendada]
          .map(v => String(v || "").trim().toLowerCase()).join("|");
        let g = porClave.get(k);
        if (!g) { g = { item, cantidad: 0, n: 0 }; porClave.set(k, g); grupos.push(g); }
        g.cantidad = Math.round((g.cantidad + (Number(item?.quantity) || 0)) * 10000) / 10000;
        g.n += 1;
      });
      return grupos.map((g, idx) => [
        String(idx + 1), g.item.material_name || "—", g.item.unit || "—",
        String(g.cantidad) + (g.n > 1 ? ` (${g.n} ítems)` : ""),
        g.item.sector || "—", g.item.front || "—", g.item.marca_recomendada || "—"
      ]);
    })(),
    headStyles: { fillColor: CEN_BLUE, textColor: [255, 255, 255], fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8.5, textColor: [30, 30, 30] },
    alternateRowStyles: { fillColor: [237, 245, 255] },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 72 }, 2: { cellWidth: 12 }, 3: { cellWidth: 12 }, 4: { cellWidth: 22 }, 5: { cellWidth: 28 }, 6: { cellWidth: 22 } },
    margin: { left: 14, right: 14 }
  });

  const history = buildHistory(req);
  if (history.length > 0) {
    const histY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_BLUE);
    doc.text("HISTORIAL DEL PEDIDO", 14, histY);

    autoTable(doc, {
      startY: histY + 3,
      head: [["Fecha y Hora", "Acción", "Responsable", "Comentario"]],
      body: history.map(h => [
        formatDateTime(h.at), h.action, h.by || "—", h.comment || ""
      ]),
      headStyles: { fillColor: [55, 65, 81], textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8, textColor: [30, 30, 30] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 48 }, 2: { cellWidth: 38 }, 3: { cellWidth: 60 } },
      margin: { left: 14, right: 14 }
    });
  }

  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text(`Generado por Sistema ERP CEN · ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });

  guardarPDF(doc, `${req.number || "pedido"}.pdf`);
}

// Fix EG (2026-09-25): una línea por material con la cantidad total, aunque el pedido lo
// reparta en varios ítems del presupuesto ("no le puedo pasar a mi proveedor 14 veces 5
// metros", CEN 25/09). Solo para mostrar e imprimir: PF y OC guardan el reparto por
// ítem, que es lo que lee el consumo del presupuesto. `conPrecio` (PF y OC) junta solo
// las líneas con el mismo precio unitario y suma también el subtotal.
const _normLinea = v => String(v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
export function agruparPorMaterial(items, conPrecio = false) {
  const grupos = new Map();
  // Fix EJ (2026-09-27): `__idx` = posiciones de las líneas que junta cada fila (para
  // editar cantidades de la OC por material).
  (items || []).forEach((it, i) => {
    if (!it) return;
    // Fix EG1 (2026-09-25): otra marca u observación es otra fila, también en PF y OC.
    const k = [_normLinea(it.material_name || it.item_name), _normLinea(it.unit), _normLinea(it.marca_recomendada),
      _normLinea(it.observacion), conPrecio ? String(Number(it.unit_price) || 0) : ""].join("|");
    const n = (it.lineas_pm || []).length || 1;
    const g = grupos.get(k);
    if (!g) { grupos.set(k, { ...it, quantity: Number(it.quantity) || 0, subtotal: Number(it.subtotal) || 0, __lineas: n, __idx: [i] }); return; }
    g.__idx.push(i);
    g.quantity = Math.round((g.quantity + (Number(it.quantity) || 0)) * 10000) / 10000;
    g.subtotal += Number(it.subtotal) || 0;
    g.__lineas += n;
  });
  return [...grupos.values()];
}

export function generatePPPDF(pp, selectedIndexes) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_BLUE = [46, 109, 164];
  doc.setFillColor(...CEN_BLUE);
  doc.rect(0, 0, pw, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 12);
  doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
  doc.text("Instalaciones Eléctricas  —  Asunción, Paraguay", 14, 19);
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text("PEDIDO DE PRESUPUESTO", pw - 14, 13, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(pp.number || "", pw - 14, 20, { align: "right" });
  doc.setTextColor(...CEN_BLUE);
  doc.setFontSize(8);
  doc.text(`PM Origen: ${pp.pm_number || "—"}`, 14, 30);
  doc.text(`Proyecto: ${pp.project_name || "—"}`, pw - 14, 30, { align: "right" });
  let y = 42;
  doc.setFontSize(8.5);
  const col2 = pw / 2 + 4;
  const fieldRow = (x, fy, label, value) => {
    doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 80);
    doc.text(label + ":", x, fy);
    doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20);
    doc.text(String(value || "—"), x + 32, fy);
  };
  // BLOQUE 14 PARTE B — identificar la obra en el PDF del PP (pedido cliente 2026-07-20):
  // Obra (catálogo, heredada del PM), Cliente y Presupuesto vinculado.
  fieldRow(14, y, "Obra", pp.lista_obra_nombre || pp.project_name);
  fieldRow(col2, y, "Cliente", pp.lista_cliente_nombre);
  fieldRow(14, y + 7, "Presupuesto", pp.presupuesto_codigo);
  fieldRow(col2, y + 7, "Generado por", pp.created_by_name);
  fieldRow(14, y + 14, "Fecha", formatDateTime(pp.created_at));
  doc.setDrawColor(...CEN_BLUE);
  doc.setLineWidth(0.3);
  doc.line(14, y + 20, pw - 14, y + 20);
  y += 24;
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_BLUE);
  doc.text("MATERIALES — SOLICITAR COTIZACIÓN A PROVEEDOR", 14, y);
  // BLOQUE 18 PARTE A — filtro de exportación: si viene `selectedIndexes`, solo esos
  // ítems entran al PDF (renumerados 1..N, sin huecos). Sin selección -> todos (default
  // idéntico al comportamiento histórico).
  const allItems = pp.items || [];
  // Fix EG (2026-09-25): una línea por material con la cantidad total.
  const exportItems = agruparPorMaterial(Array.isArray(selectedIndexes) ? selectedIndexes.map(i => allItems[i]).filter(Boolean) : allItems);
  autoTable(doc, {
    startY: y + 3,
    head: [["#", "Descripción", "UM", "Cant.", "Marca Recomendada", "P.U. Ofertado", "Subtotal"]],
    body: exportItems.map((item, idx) => [
      String(idx + 1), item.material_name || "—", item.unit || "—",
      String(item.quantity ?? "—"), item.marca_recomendada || "", "", ""
    ]),
    headStyles: { fillColor: CEN_BLUE, textColor: [255, 255, 255], fontSize: 7.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8, textColor: [30, 30, 30], minCellHeight: 10 },
    alternateRowStyles: { fillColor: [237, 245, 255] },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 65 }, 2: { cellWidth: 12 }, 3: { cellWidth: 12 }, 4: { cellWidth: 35 }, 5: { cellWidth: 25 }, 6: { cellWidth: 19 } },
    margin: { left: 14, right: 14 }
  });
  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text(`Generado por Sistema ERP CEN · ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });
  guardarPDF(doc, `${pp.number || "pp"}.pdf`);
}

// BLOQUE 24 — Solicitud de Retiro de Stock Interno (RSI): documento imprimible/aval
// para los ítems del PM imputados a stock interno (material o herramienta) en vez de
// comprarse a proveedor. `rsi` viene del registro persistido por el backend
// (POST /purchases/stock-withdrawals) — NO descuenta inventario, es solo el aval.
export function generateRetiroStockPDF(rsi) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_PURPLE = [124, 58, 237];
  doc.setFillColor(...CEN_PURPLE);
  doc.rect(0, 0, pw, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 12);
  doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
  doc.text("Instalaciones Eléctricas  —  Asunción, Paraguay", 14, 19);
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text("SOLICITUD DE RETIRO DE STOCK INTERNO", pw - 14, 13, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(rsi.number || "", pw - 14, 20, { align: "right" });
  doc.setTextColor(...CEN_PURPLE);
  doc.setFontSize(8);
  doc.text(`PM Origen: ${rsi.pm_number || "—"}`, 14, 30);
  // Fix EJ (2026-09-27): la solicitud generada desde un Análisis de Presupuesto lo indica.
  doc.text(rsi.ap_number ? `PP: ${rsi.pp_number || "—"} · AP: ${rsi.ap_number}` : (rsi.pp_number ? `PP: ${rsi.pp_number}` : "Sin PP (cubierto 100% por stock)"), pw - 14, 30, { align: "right" });

  let y = 42;
  doc.setFontSize(8.5);
  const col2 = pw / 2 + 4;
  const fieldRow = (x, fy, label, value) => {
    doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 80);
    doc.text(label + ":", x, fy);
    doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20);
    doc.text(String(value || "—"), x + 32, fy);
  };
  fieldRow(14, y, "Obra", rsi.lista_obra_nombre || rsi.project_name);
  fieldRow(col2, y, "Cliente", rsi.lista_cliente_nombre);
  fieldRow(14, y + 7, "Presupuesto", rsi.presupuesto_codigo);
  fieldRow(col2, y + 7, "Solicitante", rsi.created_by_name);
  fieldRow(14, y + 14, "Fecha", formatDateTime(rsi.created_at));
  doc.setDrawColor(...CEN_PURPLE);
  doc.setLineWidth(0.3);
  doc.line(14, y + 20, pw - 14, y + 20);
  y += 24;
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...CEN_PURPLE);
  doc.text("ÍTEMS A RETIRAR DE STOCK INTERNO", 14, y);

  const items = rsi.items || [];
  autoTable(doc, {
    startY: y + 3,
    // Fix EQ (2026-09-30): la marca del material de inventario, para leerlo mejor.
    head: [["#", "Tipo", "Descripción", "Ref. Inventario", "Marca", "Cant.", "UM"]],
    body: items.map((item, idx) => [
      String(idx + 1),
      item.origen === "stock_herramientas" ? "Herramienta" : "Material",
      item.material_name || "—",
      item.origen_material_code ? `${item.origen_material_code} · ${item.origen_material_name || ""}` : "—",
      item.origen_material_marca || "—",
      String(item.quantity ?? "—"), item.unit || "—"
    ]),
    headStyles: { fillColor: CEN_PURPLE, textColor: [255, 255, 255], fontSize: 8.5, fontStyle: "bold" },
    // BLOQUE 26 PARTE C (preventivo) — mismo cuidado de anchos que el Acta (Bloque 25A):
    // descripciones/códigos envuelven por palabra (overflow linebreak) y cantidad/UM con
    // ancho suficiente + overflow visible para que un número corto nunca se parta.
    bodyStyles: { fontSize: 8, textColor: [30, 30, 30], overflow: "linebreak" },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 20 },
      2: { cellWidth: 50, overflow: "linebreak" },
      3: { cellWidth: 48, overflow: "linebreak" },
      4: { cellWidth: 22, overflow: "linebreak" },
      5: { cellWidth: 12, halign: "center", overflow: "visible" },
      6: { cellWidth: 12, halign: "center", overflow: "visible" }
    },
    margin: { left: 14, right: 14 },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 1) {
        const isTool = data.cell.raw === "Herramienta";
        data.cell.styles.fillColor = isTool ? [255, 247, 230] : [237, 245, 255];
        data.cell.styles.textColor = isTool ? [154, 90, 10] : [30, 64, 122];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  let signY = doc.lastAutoTable.finalY + 26;
  const pageH = doc.internal.pageSize.getHeight();
  if (signY > pageH - 30) signY = pageH - 30;
  const sigLabels = ["Solicita", "Autoriza", "Entrega de depósito"];
  const sigW = (pw - 28) / 3;
  doc.setDrawColor(120, 120, 120); doc.setLineWidth(0.2);
  sigLabels.forEach((label, i) => {
    const x0 = 14 + i * sigW;
    doc.line(x0, signY, x0 + sigW - 10, signY);
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 80);
    doc.text(label, x0, signY + 5);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
    doc.text("Nombre, firma y fecha", x0, signY + 10);
  });

  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text(`Este documento NO descuenta inventario — solo constituye el aval de solicitud · Generado: ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });
  guardarPDF(doc, `${rsi.number || "rsi"}.pdf`);
}

// BLOQUE 18 PARTE B — allocations[] es la fuente de verdad para el reparto entre
// proveedores; retro-compat con AP cerrados antes de este bloque (solo selected_idx).
function _itemAllocations(item) {
  const allocs = item.allocations;
  if (Array.isArray(allocs)) return allocs;
  const idx = item.selected_idx ?? -1;
  if (typeof idx === "number" && idx >= 0) return [{ provider_idx: idx, quantity: item.quantity || 0 }];
  return [];
}

// BLOQUE 25 PARTE A — `options.visibleProviderIdxs` / `options.visibleItemIdxs` (ambos
// opcionales): índices ORIGINALES de act.provider_columns / act.analysis_items que deben
// entrar en el PDF. Sin ninguno de los dos (o vacíos) -> todas las columnas Y todas las
// filas, comportamiento idéntico al histórico (regresión Bloque 19/20/23). Filtro
// EXCLUSIVO de exportación: act NUNCA se modifica, las allocations/precios se leen
// siempre por el índice ORIGINAL del proveedor/ítem.
export function generateAPPDF(act, options) {
  const opts = options || {};
  const allCols = act.provider_columns || [];
  const allItems = act.analysis_items || [];
  const visibleIdxs = Array.isArray(opts.visibleProviderIdxs) && opts.visibleProviderIdxs.length > 0
    ? opts.visibleProviderIdxs.filter(i => i >= 0 && i < allCols.length)
    : allCols.map((_, i) => i);
  const visibleItemIdxs = Array.isArray(opts.visibleItemIdxs) && opts.visibleItemIdxs.length > 0
    ? opts.visibleItemIdxs.filter(i => i >= 0 && i < allItems.length)
    : allItems.map((_, i) => i);
  const cols = visibleIdxs.map(i => allCols[i]);
  const items = visibleItemIdxs.map(i => allItems[i]);
  // Bloque 19: siempre horizontal y compacto (2 columnas por proveedor) para que
  // entren varios proveedores sin que autoTable rompa el texto letra por letra.
  // Fix CX (2026-09-14): "se podría habilitar la opción de imprimir PDF también en la
  // comparativa auxiliar" (Sergio, 14/09). La comparativa auxiliar tiene exactamente el
  // formato del Análisis de Presupuestos, así que imprime con este MISMO generador: solo
  // cambian el encabezado (no tiene número, PP ni proyecto) y cómo se suman los totales.
  // Sin la bandera, todo se comporta igual que siempre para los AP.
  const esAux = opts.comparativaAuxiliar === true;
  const doc = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const CEN_PURPLE = [109, 40, 217];
  doc.setFillColor(...CEN_PURPLE);
  doc.rect(0, 0, pw, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text("CEN INGENIERIA E.A.S.", 14, 11);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text("Instalaciones Eléctricas — Asunción, Paraguay", 14, 18);
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  doc.text(esAux ? "COMPARATIVA AUXILIAR" : "ACTA DE PRESUPUESTO COMPARATIVA", pw - 14, 11, { align: "right" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(esAux ? formatDate(act.updated_at || act.created_at) : (act.number || ""), pw - 14, 18, { align: "right" });
  // Fix EX (2026-10-05): "al imprimir un análisis de pto que se indique también el nombre de la
  // obra" (CEN). Esta línea iba en violeta sobre la franja violeta y no se leía: va en blanco.
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  if (esAux) {
    doc.text(String(act.titulo || "Comparativa"), 14, 27);
    // Queda impreso que es un borrador: la hoja no se puede confundir con un AP del
    // proceso de compra, que es lo único que autoriza una orden.
    doc.text("Borrador fuera del proceso de compra · no genera pedidos ni órdenes", pw - 14, 27, { align: "right" });
  } else {
    const obra = act.project_name || act.obra_rapida_nombre || act.lista_obra_nombre || "—";
    const cliente = act.lista_cliente_nombre ? `   ·   Cliente: ${act.lista_cliente_nombre}` : "";
    doc.setFont("helvetica", "bold");
    doc.text(`Obra: ${obra}`, 14, 26);
    doc.setFont("helvetica", "normal");
    doc.text(`   ·   PP: ${act.pp_number || "—"}${cliente}`, 14 + doc.getTextWidth(`Obra: ${obra}`), 26);
    if (act.status === "cerrada") doc.text(`Estado: CERRADA · ${formatDateTime(act.closed_at)}`, pw - 14, 26, { align: "right" });
  }
  if (cols.length === 0) {
    doc.setTextColor(100, 100, 100); doc.setFontSize(10);
    doc.text("Sin datos de análisis cargados", pw / 2, 60, { align: "center" });
  } else {
    // BLOQUE 46 — con muchas columnas (6-8 proveedores) el auto-ajuste anterior
    // reducía tanto el ancho que los ENCABEZADOS (UM, Cant., Cant. Acum., Subtotal)
    // y la etiqueta TOTAL se partían letra por letra: sus anchos base (BASE.um/
    // cant/acum/qty) nunca consideraban el ancho real de su propio texto — a
    // diferencia de P.U./Subtotal, que sí se miden desde los montos reales. Con 5+
    // proveedores se abrevia (Subtotal->Subt., Cant.->Ct. en la columna del
    // proveedor, Cant. Acum.->Acum.) para necesitar menos espacio; con 2-4
    // proveedores el texto completo entra igual que siempre (sin cambios visuales).
    const compactHeaders = cols.length >= 5;
    // Fix CX (2026-09-14): en una comparativa auxiliar no hay adjudicación parcial —
    // esa columna es la cantidad del proveedor elegido, y así se llama.
    const acumLabel = esAux ? "Elegido" : (compactHeaders ? "Acum." : "Cant. Acum.");
    const provCantLabel = compactHeaders ? "Ct." : "Cant.";
    const provSubtotalLabel = compactHeaders ? "Subt." : "Subtotal";
    // BLOQUE 45 PARTE B — 3 columnas por proveedor (P.U. / Cant. / Subtotal), se
    // recupera el Subtotal pedido por el cliente. Encabezado agrupado en 2 filas: el
    // nombre del proveedor ocupa todo el bloque para que no se corte letra por letra
    // en columnas angostas (Bloque 19).
    const headers = [
      [
        { content: "Ítem", rowSpan: 2 },
        { content: "UM", rowSpan: 2 },
        { content: "Cant.", rowSpan: 2 },
        { content: acumLabel, rowSpan: 2 },
        // BLOQUE 36 — proveedores con cierre parcial se marcan "(CERRADO)" en el
        // encabezado de su columna; sin otros cambios de layout.
        ...cols.flatMap((c, ci) => {
          const origIdx = visibleIdxs[ci];
          const isClosed = (act.closed_providers || []).some(cp => cp.provider_idx === origIdx);
          return [{ content: isClosed ? `${c} (CERRADO)` : c, colSpan: 3, styles: { halign: "center" } }];
        }),
        { content: "Proveedor(es)", rowSpan: 2 }
      ],
      // BLOQUE 45 PARTE B — se recupera la columna "Subtotal" (P.U. × cantidad
      // adjudicada) por proveedor, pedida por el cliente tras el recorte del Bloque 19.
      cols.flatMap(() => ["P.U.", provCantLabel, provSubtotalLabel])
    ];
    const ACUM_FILL = { blue: [219, 234, 254], green: [220, 252, 231], red: [254, 226, 226] };
    const ACUM_TEXT = { blue: [30, 64, 175], green: [22, 101, 52], red: [153, 27, 27] };
    const rowMeta = [];
    const body = items.map(item => {
      const allocations = _itemAllocations(item);
      const visibleAllocations = allocations.filter(a => visibleIdxs.includes(a.provider_idx));
      const solicited = Number(item.quantity) || 0;
      const accum = visibleAllocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
      const acumStatus = accum === solicited ? "blue" : (accum > solicited ? "green" : "red");
      const allocatedFlags = cols.map((_, ci) => {
        const origIdx = visibleIdxs[ci];
        const alloc = allocations.find(a => a.provider_idx === origIdx);
        return !!(alloc && Number(alloc.quantity) > 0);
      });
      // Fix EJ (2026-09-27): % del P.U. de cada proveedor contra la referencia del presupuesto.
      const semaforos = cols.map((_, ci) => {
        const pu = ((item.prices || [])[visibleIdxs[ci]] || {}).unit_price || 0;
        return !esAux && item.ref_precio_mat > 0 && pu > 0 ? ((pu / item.ref_precio_mat) - 1) * 100 : null;
      });
      rowMeta.push({ acumStatus, allocatedFlags, semaforos });
      // CEN 2.0 FASE 3 PARTE C — 2ª línea informativa con el costo de referencia del
      // presupuesto y el % de diferencia vs el P.U. del primer proveedor adjudicado.
      // Solo aparece si el ítem tiene ref_precio_mat (AP nuevo, con item_uid); los AP
      // históricos sin esa referencia no cambian de aspecto (cero regresión).
      // Fix EJ (2026-09-27): el % va ahora debajo del P.U. de cada proveedor.
      const refSuffix = item.ref_precio_mat ? `\nRef: ${formatPYG(item.ref_precio_mat)}` : "";
      // Fix CK (2026-09-11): marca y observación que vienen del pedido de materiales.
      const pedidoSuffix = [item.marca_recomendada && `Marca: ${item.marca_recomendada}`, item.observacion && `Obs.: ${item.observacion}`].filter(Boolean).join(" · ");
      const row = [`${item.item_name || "—"}${pedidoSuffix ? `\n${pedidoSuffix}` : ""}${refSuffix}`, item.unit || "—", String(item.quantity ?? "—"), String(accum || 0)];
      cols.forEach((_, ci) => {
        const origIdx = visibleIdxs[ci];
        const p = (item.prices || [])[origIdx] || {};
        const alloc = allocations.find(a => a.provider_idx === origIdx);
        const allocQty = alloc ? (Number(alloc.quantity) || 0) : 0;
        // Fix CX (2026-09-14): la comparativa auxiliar se imprime para COMPARAR, así que
        // cada proveedor muestra su subtotal por la cantidad del ítem, esté elegido o no
        // —igual que la pantalla—. En un AP el subtotal sigue siendo solo lo adjudicado.
        const subtotal = esAux
          ? (p.unit_price > 0 ? p.unit_price * (Number(item.quantity) || 0) : 0)
          : (alloc && p.unit_price > 0 ? p.unit_price * allocQty : 0);
        const pct = semaforos[ci];
        row.push(p.unit_price > 0 ? `${formatPYG(p.unit_price)}${pct !== null ? `\n${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : ""}` : "—");
        row.push(alloc ? String(allocQty) : "—");
        row.push(subtotal > 0 ? formatPYG(subtotal) : "—");
      });
      // Ítem adjudicado a un proveedor OCULTO en este PDF -> sin ganador visible ("—"),
      // nunca se inventa otro ganador. "Sin selec." solo si el ítem no tiene ninguna
      // adjudicación (visible ni oculta).
      row.push(
        visibleAllocations.length
          ? visibleAllocations.map(a => `${allCols[a.provider_idx] || "—"} (${a.quantity})`).join(" + ")
          : (allocations.length ? "—" : "Sin selec.")
      );
      return row;
    });
    // Fix CK (2026-09-11): "al imprimir que se impriman también las obs de cada columna"
    // (Sergio, 11/09). Debajo de cada ítem con alguna observación de proveedor va una fila con
    // la obs. de cada proveedor bajo su propio bloque. No cambia el ancho de ninguna columna.
    for (let k = items.length - 1; k >= 0; k--) {
      const obs = cols.map((_, ci) => String(((items[k].prices || [])[visibleIdxs[ci]] || {}).obs || "").trim());
      if (!obs.some(Boolean)) continue;
      const estilo = { fontStyle: "italic", textColor: [90, 90, 90], fillColor: [255, 255, 255], overflow: "linebreak", halign: "left" };
      body.splice(k + 1, 0, [
        { content: "Obs. de proveedores", colSpan: 4, styles: { ...estilo, halign: "right" } },
        ...obs.map(o => ({ content: o ? `Obs.: ${o}` : "", colSpan: 3, styles: estilo })),
        { content: "", styles: estilo },
      ]);
      rowMeta.splice(k + 1, 0, null);
    }
    const totalsRowIndex = body.length;
    // BLOQUE 20 — fila TOTAL construida por índice explícito (no por push secuencial) para
    // garantizar que cada monto caiga exactamente bajo la columna P.U. de su proveedor, y el
    // total del pie se calcula en el propio PDF a partir de las allocations (nunca desde
    // act.selected_total, que puede quedar desactualizado/0 en APs viejos).
    const totalColCount = 4 + cols.length * 3 + 1;
    const totals = new Array(totalColCount).fill("");
    totals[2] = "TOTAL";
    totals[3] = "—";
    let grandTotal = 0;
    // Fix CX (2026-09-14): en la comparativa auxiliar el total de la última columna es el
    // TOTAL SELECCIONADO (lo que se elegiría comprando el mejor precio de cada ítem), que
    // es el número que muestra la pantalla. Sumar los totales de todos los proveedores no
    // querría decir nada.
    // Fix EI (2026-09-27): con cantidades por proveedor, lo elegido es Σ P.U. × cantidad
    // asignada (una comparativa vieja se lee como la cantidad entera al elegido).
    const totalElegido = items.reduce((s, item) => s + _itemAllocations(item).reduce((t, a) =>
      t + ((item.prices || [])[a.provider_idx]?.unit_price || 0) * (Number(a.quantity) || 0), 0), 0);
    cols.forEach((_, ci) => {
      const origIdx = visibleIdxs[ci];
      // Total por proveedor usando la cantidad efectivamente adjudicada (allocation), no el total del ítem
      const t = items.reduce((s, item) => {
        // Fix CX (2026-09-14): en la comparativa, el total de cada proveedor es su canasta
        // completa —todos los ítems por su cantidad—, que es lo que se está comparando.
        const alloc = _itemAllocations(item).find(a => a.provider_idx === origIdx);
        const qty = esAux ? (Number(item.quantity) || 0) : (alloc ? (Number(alloc.quantity) || 0) : 0);
        return s + ((item.prices || [])[origIdx]?.unit_price || 0) * qty;
      }, 0);
      grandTotal += t;
      // BLOQUE 45 PARTE B — el total por proveedor ahora se muestra bajo su propia
      // columna "Subtotal" (antes vivía bajo P.U. por no existir esa columna).
      totals[4 + ci * 3 + 2] = t > 0 ? formatPYG(t) : "—";
    });
    const totalFinal = esAux ? totalElegido : grandTotal;   // Fix CX (2026-09-14)
    totals[totalColCount - 1] = totalFinal > 0 ? formatPYG(totalFinal) : "—";
    body.push(totals);
    // Fix EJ (2026-09-27): "al imprimir algún análisis, que se muestren los porcentajes por
    // encima o por abajo. Monto por encima de los precios de referencia" (CEN). Por
    // proveedor: Σ (P.U. − referencia) × cantidad adjudicada, en los ítems con referencia.
    const difGs = (v) => `${v > 0 ? "+" : v < 0 ? "-" : ""}${formatPYG(Math.abs(v))}`;
    let refTotal = 0;
    const difs = cols.map((_, ci) => items.reduce((s, it) => {
      const alloc = _itemAllocations(it).find(a => a.provider_idx === visibleIdxs[ci]);
      const pu = ((it.prices || [])[visibleIdxs[ci]] || {}).unit_price || 0;
      if (esAux || !(it.ref_precio_mat > 0) || !alloc || !(pu > 0)) return s;
      const q = Number(alloc.quantity) || 0;
      refTotal += it.ref_precio_mat * q;
      return s + (pu - it.ref_precio_mat) * q;
    }, 0));
    const difTotal = difs.reduce((s, v) => s + v, 0);
    const difStr = difs.map(v => (v ? difGs(v) : "—"));
    const difTotalStr = refTotal > 0 ? `${difGs(difTotal)} (${difTotal > 0 ? "+" : ""}${((difTotal / refTotal) * 100).toFixed(1)}%)` : "";
    const difRowIndex = refTotal > 0 ? body.length : -1;
    if (difRowIndex >= 0) {
      body.push([
        { content: "Diferencia vs. referencia (lo adjudicado)", colSpan: 4, styles: { halign: "right" } },
        ...cols.flatMap((_, ci) => ["", "", difStr[ci]]),
        difTotalStr,
      ]);
    }

    // BLOQUE 25 PARTE A — anchos AUTOMÁTICOS: los anchos "base" fueron pensados para
    // pocos proveedores; con 4+ proveedores (o descripciones largas) la suma podía superar
    // el ancho útil de la página y el texto de columnas contiguas se solapaba (reporte
    // cliente 2026-07-23). Si la suma base excede el ancho útil, se escala TODO
    // proporcionalmente (columnas + tamaño de fuente, con piso mínimo legible) para que la
    // tabla SIEMPRE entre dentro de los márgenes — nunca se pisan columnas, sin importar
    // cuántos proveedores/ítems tenga el AP. Con 1-3 proveedores (caso histórico) el ancho
    // base ya entra -> scale=1 -> anchos IDÉNTICOS al comportamiento anterior (regresión).
    const proveedoresColIndex = 4 + cols.length * 3;
    const pageMargin = 6;
    const usableWidth = pw - pageMargin * 2;
    const BASE = { item: 46, um: 9, cant: 12, acum: 14, prov: 40, pu: 18, qty: 11, subtotal: 20 };

    // BLOQUE 26 PARTE A — el ancho fijo BASE.pu solo consideraba las filas de ÍTEMS
    // (valores de 5-6 dígitos). Se mide con jsPDF el texto REAL más ancho de cada
    // columna de importe a la fuente base y se usa el mayor entre el ancho histórico y
    // el medido. BLOQUE 45 PARTE B — la columna Subtotal (nueva) recibe el MISMO
    // tratamiento, y además hereda la medición del TOTAL por proveedor del pie (que
    // ahora vive en esta columna, ver más abajo) para que un importe grande (7-8
    // dígitos) nunca se envuelva en 2 líneas.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    const textW = (s) => doc.getTextWidth(String(s ?? ""));
    // BLOQUE 46 — CELL_PAD subido de 4 a 6: el testing real (iteration_140) detectó un
    // solapamiento de ~1.3mm entre la columna Ct. (en negrita cuando hay adjudicación)
    // y la columna Subtotal vecina en el caso más exigente (8 proveedores + un P.U. de
    // 8 dígitos) — el margen anterior era insuficiente para absorber el ancho extra del
    // texto en negrita. Con más colchón, "overflow: visible" (Bloque 26) ya no necesita
    // activarse en la práctica: el ancho medido alcanza siempre para el texto real.
    const CELL_PAD = 6;
    const puWidths = cols.map((_, ci) => {
      const origIdx = visibleIdxs[ci];
      let maxW = 0;
      items.forEach(item => {
        const p = (item.prices || [])[origIdx] || {};
        if (p.unit_price > 0) maxW = Math.max(maxW, textW(formatPYG(p.unit_price)));
      });
      return Math.max(BASE.pu, maxW + CELL_PAD);
    });
    const subtotalWidths = cols.map((_, ci) => {
      const origIdx = visibleIdxs[ci];
      let maxW = Math.max(textW(totals[4 + ci * 3 + 2] || ""), difRowIndex >= 0 ? textW(difStr[ci]) : 0); // TOTAL por proveedor del pie (y Fix EJ: su diferencia vs. referencia)
      items.forEach(item => {
        const p = (item.prices || [])[origIdx] || {};
        const alloc = _itemAllocations(item).find(a => a.provider_idx === origIdx);
        const qty = alloc ? (Number(alloc.quantity) || 0) : 0;
        const sub = p.unit_price > 0 ? p.unit_price * qty : 0;
        if (sub > 0) maxW = Math.max(maxW, textW(formatPYG(sub)));
      });
      return Math.max(BASE.subtotal, maxW + CELL_PAD);
    });
    // La celda de TOTAL en la última columna ("Proveedor(es)") reutiliza esa columna para
    // el total general — debe entrar sin cortarse aunque el resto de filas use ancho por
    // texto/wrap normal (nunca se reduce por debajo del ancho histórico BASE.prov).
    const provWidth = Math.max(BASE.prov, textW(totals[totalColCount - 1]) + CELL_PAD, textW(difTotalStr) + CELL_PAD);   // Fix EJ
    // BLOQUE 46 R2 — RCA exacto de iteration_141: medir "TOTAL"/etiquetas a un
    // fontSize FIJO (6.5/6) y sumarlas a un ancho que luego SÍ se escala por
    // `scale` asume que el texto también se va a renderizar a 6.5*scale — pero
    // bodyFontSize/headFontSize tienen un PISO (5/4.5) que no baja en la misma
    // proporción que el ancho de columna una vez que 6.5*scale (o 6*scale) cae por
    // debajo del piso. Con 8 proveedores (scale≈0.54) eso da un déficit real de
    // ~0.56mm y "TOTAL" en negrita se sigue envolviendo en 2 líneas. Fix: una vez
    // que `scale`/`bodyFontSize`/`headFontSize` FINALES están calculados, se vuelve
    // a medir cada etiqueta crítica al fontSize REAL de renderizado (ya con el piso
    // aplicado) y se usa el MAYOR entre el ancho ya escalado y ese ancho real+
    // padding — sin asumir proporcionalidad, así nunca puede quedar corto sin
    // importar dónde caiga el piso.
    doc.setFont("helvetica", "bold");
    const totalLabelW = textW("TOTAL");
    doc.setFont("helvetica", "normal");
    const umBase = Math.max(BASE.um, textW("UM") + CELL_PAD);
    const cantBase = Math.max(BASE.cant, textW("Cant.") + CELL_PAD, totalLabelW + CELL_PAD);
    const acumBase = Math.max(BASE.acum, textW(acumLabel) + CELL_PAD);
    const provCantBase = Math.max(BASE.qty, textW(provCantLabel) + CELL_PAD);
    const baseTotal = BASE.item + umBase + cantBase + acumBase + provWidth
      + cols.reduce((s, _, ci) => s + puWidths[ci] + provCantBase + subtotalWidths[ci], 0);
    const scale = baseTotal > usableWidth ? usableWidth / baseTotal : 1;
    const w = (v) => Math.round(v * scale * 100) / 100;
    // Piso de fuente = mismos valores validados en el Bloque 45 (5/4.5) — NO subirlos:
    // el testing real (iteration_140) confirmó que un piso más alto (5.2/5) rompe la
    // proporción ancho↔fuente en el caso más ajustado (8 proveedores) y reintroduce
    // solapamientos, porque los anchos de P.U./Subtotal se calculan asumiendo que la
    // fuente se reduce en la MISMA proporción que el ancho (Bloque 26).
    const bodyFontSize = scale < 1 ? Math.max(6.5 * scale, 5) : 6.5;
    const headFontSize = scale < 1 ? Math.max(6 * scale, 4.5) : 6;
    // Re-medición al fontSize REAL final (post-piso) — este es el paso que faltaba
    // en el intento anterior (iteration_141): garantiza el ancho SIN depender de
    // ninguna proporción entre `scale` y el fontSize, midiendo directo con el
    // tamaño con el que jsPDF va a dibujar el texto.
    const REAL_PAD = 2.6; // cellPadding=1/lado (headStyles/bodyStyles) + margen mínimo
    doc.setFont("helvetica", "bold");
    doc.setFontSize(bodyFontSize);
    const totalLabelWReal = textW("TOTAL");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(headFontSize);
    const umLabelWReal = textW("UM");
    const cantLabelWReal = textW("Cant.");
    const acumLabelWReal = textW(acumLabel);
    const provCantLabelWReal = textW(provCantLabel);
    const umW = Math.max(w(umBase), umLabelWReal + REAL_PAD);
    const cantW = Math.max(w(cantBase), cantLabelWReal + REAL_PAD, totalLabelWReal + REAL_PAD);
    const acumW = Math.max(w(acumBase), acumLabelWReal + REAL_PAD);
    const provCantW = Math.max(w(provCantBase), provCantLabelWReal + REAL_PAD);
    const columnStyles = {
      0: { cellWidth: w(BASE.item), overflow: "linebreak", halign: "left" },
      1: { cellWidth: umW, halign: "center" },
      2: { cellWidth: cantW, halign: "center" },
      3: { cellWidth: acumW, halign: "center" },
      [proveedoresColIndex]: { cellWidth: w(provWidth), overflow: "linebreak", halign: "left" }
    };
    cols.forEach((_, ci) => {
      // overflow: 'visible' — el ancho ya se dimensionó para el valor más ancho de la
      // columna (incluida la fila TOTAL); 'visible' es el resguardo final para que un
      // importe NUNCA se envuelva en 2 líneas por un redondeo de medición.
      columnStyles[4 + ci * 3] = { cellWidth: w(puWidths[ci]), halign: "right", overflow: "visible" };
      columnStyles[4 + ci * 3 + 1] = { cellWidth: provCantW, halign: "center" };
      columnStyles[4 + ci * 3 + 2] = { cellWidth: w(subtotalWidths[ci]), halign: "right", overflow: "visible" };
    });

    autoTable(doc, {
      startY: 34,
      head: headers,
      body,
      headStyles: { fillColor: CEN_PURPLE, textColor: [255, 255, 255], fontSize: headFontSize, fontStyle: "bold", halign: "center", cellPadding: 1 },
      bodyStyles: { fontSize: bodyFontSize, textColor: [30, 30, 30], overflow: "linebreak", cellPadding: 1 },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      columnStyles,
      margin: { left: pageMargin, right: pageMargin },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        // Fix EJ (2026-09-27): rojo por encima de la referencia, verde por debajo.
        if (data.row.index === difRowIndex) {
          const k = data.column.index - 6;
          const v = data.column.index === proveedoresColIndex ? difTotal : (k >= 0 && k % 3 === 0 && k / 3 < cols.length ? difs[k / 3] : 0);
          if (v) data.cell.styles.textColor = v > 0 ? [185, 28, 28] : [21, 128, 61];
          return;
        }
        if (data.row.index === totalsRowIndex) {
          if (data.column.index === 2) data.cell.styles.fontStyle = "bold";
          return;
        }
        const meta = rowMeta[data.row.index];
        if (!meta) return;
        // Cant. Acum. — semáforo azul/verde/rojo
        if (data.column.index === 3) {
          data.cell.styles.fillColor = ACUM_FILL[meta.acumStatus];
          data.cell.styles.textColor = ACUM_TEXT[meta.acumStatus];
          data.cell.styles.fontStyle = "bold";
        }
        // Resaltar Cant. del proveedor adjudicado (allocation > 0)
        cols.forEach((_, ci) => {
          const qtyColIndex = 4 + ci * 3 + 1;
          if (data.column.index === qtyColIndex && meta.allocatedFlags[ci]) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [237, 233, 254];
          }
        });
        // Fix EJ (2026-09-27): P.U. con el color de su % contra la referencia (como en pantalla).
        cols.forEach((_, ci) => {
          const pct = (meta.semaforos || [])[ci];
          if (data.column.index === 4 + ci * 3 && pct !== null && pct !== undefined) {
            data.cell.styles.fillColor = pct > 1 ? [254, 226, 226] : pct < -1 ? [220, 252, 231] : [254, 243, 199];
          }
        });
      }
    });
    // Fix EJ (2026-09-27): qué es el % debajo de cada P.U.
    if (!esAux && items.some(it => it.ref_precio_mat > 0)) {
      const yLeyenda = doc.lastAutoTable.finalY + 4;
      if (yLeyenda < doc.internal.pageSize.getHeight() - 11) {
        doc.setFontSize(6.5); doc.setFont("helvetica", "italic"); doc.setTextColor(90, 90, 90);
        doc.text("% debajo de cada P.U.: diferencia contra el costo de referencia del presupuesto (con IVA). Rojo: por encima · Verde: por debajo · Amarillo: ±1%.", pageMargin, yLeyenda);
      }
    }
  }
  // Fix FD (2026-10-07): lo provisto desde el stock propio (en el Pedido a Proveedores o en el análisis).
  const stockPropio = act.stock_propio || [];
  if (!esAux && stockPropio.length) {
    let y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 72;
    if (y > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...CEN_PURPLE);
    doc.text("PROVISTO DESDE EL STOCK PROPIO", 14, y - 3);
    autoTable(doc, {
      startY: y,
      head: [["Descripción", "UM", "Cant.", "Material del inventario", "Etapa", "Solicitud de retiro", "Estado"]],
      body: stockPropio.map(f => [f.material_name, f.unit, String(f.quantity), f.inventario || "—", f.etapa, f.solicitud || "—", f.estado || "—"]),
      headStyles: { fillColor: [15, 118, 110], textColor: [255, 255, 255], fontSize: 6.5, fontStyle: "bold" },
      bodyStyles: { fontSize: 6.5, textColor: [30, 30, 30], cellPadding: 1 },
      columnStyles: { 2: { halign: "center", fontStyle: "bold" } },
      margin: { left: 14, right: 14 },
    });
  }
  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text(`Generado por Sistema ERP CEN · ${new Date().toLocaleString("es-PY")}`, pw / 2, footerY, { align: "center" });
  // Fix CX (2026-09-14): la comparativa auxiliar no tiene número; se guarda con su título.
  guardarPDF(doc, esAux ? `${String(act.titulo || "comparativa").trim() || "comparativa"}.pdf` : `${act.number || "ap"}.pdf`);
}
